import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
// import CommentForm from "./CommentForm"; // Assuming path
import AddComment from "./AddComment"; // Use AddComment instead

dayjs.extend(relativeTime);

const Comments = ({ comments, user, player_id, playersList, t_controls, onEditClick: parentOnEditClick, onReplyClick: parentOnReplyClick, onCommentsChange, privateTab, setPrivateTab, editedComment, replyToComment, isPlayerPage, onCommentSubmit, event_id }) => {
  const [publicCommentsCount, setPublicCommentsCount] = useState(0);
  const [privateCommentsCount, setPrivateCommentsCount] = useState(0);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [activeEditFormId, setActiveEditFormId] = useState(null); // State for inline edit form
  const [activeReplyFormId, setActiveReplyFormId] = useState(null); // State for inline reply form
  const modalRef = useRef(null);
  let modalInstance = useRef(null);

  // Check if admin has associated player
  const hasAssociatedPlayer = user?.admin ? !!user.player_id : true;

  // If admin with no player_id and on player page, return null
  if (isPlayerPage && user?.admin && !hasAssociatedPlayer) {
    return null;
  }

  useEffect(() => {
    // Count public comments
    const publicCount = comments.reduce((count, comment) =>
      (!comment.is_private) ? count + 1 : count
      , 0);

    // Count private comments that the user can see
    const privateCount = comments.reduce((count, comment) => {
      if (!comment.is_private) return count;

      // For admin users, only show private comments where their player is involved
      if (user?.admin) {
        const isInvolved = user?.player_id && (
          (comment.user && comment.user.player_id === user.player_id) ||
          comment.recipient_id === user.player_id
        );
        return isInvolved ? count + 1 : count;
      }

      // For non-admin users, show their private comments
      return ((comment.user && comment.user.id === user?.id) || comment.recipient_id === user?.id) ? count + 1 : count;
    }, 0);

    setPublicCommentsCount(publicCount);
    setPrivateCommentsCount(privateCount);
  }, [user, comments]);

  useEffect(() => {
    if (modalRef.current) {
      modalInstance.current = new bootstrap.Modal(modalRef.current);
    }
  }, []);

  const getTimeDifference = (createdAt) => {
    return dayjs(createdAt).fromNow();
  };

  const getLocaleFromURL = () => {
    const pathSegments = window.location.pathname.split("/");
    return pathSegments[1];
  };

  // Get default avatar URL for users without a photo
  const getDefaultAvatar = () => {
    return "/assets/default-avatar.png"; // Make sure this path exists in your assets
  };

  // Helper function to get recipient name from playersList
  const getRecipientName = (recipientId) => {
    if (!recipientId || !playersList) return '';

    const recipient = playersList.find(player => player.id === recipientId);
    if (!recipient) return '';

    return recipient.name && recipient.surname
      ? `${recipient.name} ${recipient.surname}`
      : recipient.email.split('@')[0];
  };

  // Helper function to get user display name
  const getUserDisplayName = (commentUser) => {
    if (!commentUser) return 'Unknown User';

    // If user is admin without player_id, show "Admin"
    if (commentUser.admin && !commentUser.player_id) {
      return 'Admin';
    }

    // If user has name and surname, use those
    if (commentUser.name && commentUser.surname) {
      return `${commentUser.name || ''} ${commentUser.surname || ''}`.trim();
    }

    // Fallback to email username
    return commentUser.email ? commentUser.email.split('@')[0] : 'Unknown User';
  };

  const handleLike = (id) => {
    const token = document.querySelector("[name=csrf-token]")?.content;
    const locale = window.location.pathname.split("/")[1];
    const base = `/${locale}`;

    axios
      .post(
        `${base}/comments/${id}/like`,
        {},
        {
          headers: {
            "X-CSRF-TOKEN": token,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        // Update the likes count display
        const likesElement = document.getElementById(`likes_${id}`);
        if (likesElement) {
          likesElement.innerText = `${response.data.data.likes_count}`;
        }

        // Update the local comments state to reflect the new like status
        const updatedComments = comments.map(comment => {
          if (comment.id === id) {
            // Create or update user_likes array if needed
            const userLikes = Array.isArray(comment.user_likes) ? [...comment.user_likes] : [];
            const hasLiked = userLikes.includes(id);

            return {
              ...comment,
              likes_count: response.data.data.likes_count,
              user_likes: hasLiked
                ? userLikes.filter(likeId => likeId !== id) // Remove like if already liked
                : [...userLikes, id] // Add like if not already liked
            };
          }
          return comment;
        });

        // Notify parent component about the change
        if (typeof onCommentsChange === 'function') {
          onCommentsChange(updatedComments);
        }
        // Server-side toast surfaced by the axios response interceptor.
      })
      .catch((error) => {
        // Toast surfaced by the axios response interceptor.
        console.error("Failed to like comment:", error);
      });
  };

  const handleDelete = (id) => {
    const comment = comments.find(c => c.id === id);
    // Extra check to prevent admin without player_id from deleting private comments
    if (user?.admin && !hasAssociatedPlayer && comment?.is_private) {
      closeModal();
      return;
    }

    const token = document.querySelector("[name=csrf-token]")?.content;
    const locale = window.location.pathname.split("/")[1];
    const base = `/${locale}`;

    axios
      .delete(`${base}/comments/${id}`, {
        headers: {
          "X-CSRF-TOKEN": token,
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        // Update the comments list by filtering out the deleted comment and its replies
        const updatedComments = comments.filter(comment =>
          comment.id !== id && comment.parent_id !== id
        );

        // Notify parent component about the change
        if (typeof onCommentsChange === 'function') {
          onCommentsChange(updatedComments);
        }

        // Server-side toast surfaced by the axios response interceptor.
        closeModal();
      })
      .catch((error) => {
        // Toast surfaced by the axios response interceptor.
        console.error("Failed to delete comment:", error);
        closeModal();
      });
  };

  const getRepliesCount = (id) => {
    let count = 0;
    const countRepliesRecursively = (commentId) => {
      comments.forEach(comment => {
        if (comment.parent_id === commentId) {
          count++;
          countRepliesRecursively(comment.id);
        }
      });
    };

    countRepliesRecursively(id);
    return count;
  };

  const getDirectReplies = (commentId) => {
    return comments.filter(comment => comment.parent_id === commentId);
  };

  const toggleReplies = (commentId) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const openModal = (id) => {
    const comment = comments.find(c => c.id === id);
    // Don't allow admin without player_id to delete private comments
    if (user?.admin && !hasAssociatedPlayer && comment?.is_private) {
      return;
    }
    setCommentToDelete(id);
    modalInstance.current?.show();
  };

  const closeModal = () => {
    setCommentToDelete(null);
    if (modalInstance.current) {
      modalInstance.current.hide();
    }
  };

  // Helper function to check if an inline form is active
  const isInlineFormActive = () => {
    return activeEditFormId !== null || activeReplyFormId !== null;
  };

  // Cancel handler for inline forms
  const handleCancelInlineForm = () => {
    setActiveEditFormId(null);
    setActiveReplyFormId(null);
    // Optionally call parent handlers if they manage state needed for main form visibility
    // parentOnEditClick(null); 
    // parentOnReplyClick(null);
  };

  // Updated onEditClick to activate inline form
  const onEditClick = (comment) => {
    setActiveEditFormId(comment.id);
    setActiveReplyFormId(null);
    // parentOnEditClick(comment); // <-- REMOVE THIS LINE (or ensure it's commented out)
  };

  // Updated onReplyClick to activate inline form
  const onReplyClick = (comment) => {
    setActiveReplyFormId(comment.id);
    setActiveEditFormId(null);
    // parentOnReplyClick(comment); // <-- REMOVE THIS LINE (or ensure it's commented out)
  };

  // Helper function to check if user can interact with comments
  const canInteractWithComments = (comment = null, action = 'view') => {
    // Disable most interactions if an inline form is active
    if (isInlineFormActive() && action !== 'view' && action !== 'toggle_replies') {
      // Allow cancelling the active form
      if ((action === 'edit' && comment?.id === activeEditFormId) || (action === 'reply' && comment?.id === activeReplyFormId)) {
        // Special case handled by the form's cancel button
        return true;
      }
      return false;
    }

    // Don't allow certain interactions on own player page EXCEPT:
    // - viewing comments
    // - replying to OTHERS' comments
    // - editing OWN comments
    if (
      isPlayerPage && player_id === user?.id && // On user's own player page
      action !== 'view' && // Action is not 'view'
      !(action === 'reply' && comment && comment.user && comment.user.id !== user?.id) && // Action is not 'reply' to someone else
      !(action === 'edit' && comment && comment.user && comment.user.id === user?.id) // Action is not 'edit' on own comment
    ) {
      return false;
    }

    // Regular users can always interact with their own comments
    if (!user?.admin) {
      // For editing, users can only edit their own comments
      if (action === 'edit') {
        return comment && comment.user && comment.user.id === user?.id;
      }
      // For replying, users can't reply to their own comments
      if (action === 'reply') {
        return comment && comment.user && comment.user.id !== user?.id;
      }
      // Don't allow creating new comments on own player page
      if (action === 'comment' && isPlayerPage && player_id === user?.id) {
        return false;
      }
      return true;
    }

    // For admins doing edit or reply actions
    if (action === 'edit') {
      // Admin can only edit if they have an associated player that is the comment author
      return hasAssociatedPlayer && comment && comment.user &&
        comment.user.id === user?.id;
    }

    // For reply actions, admin can't reply to comments made by their player
    if (action === 'reply') {
      return hasAssociatedPlayer && comment && comment.user &&
        comment.user.id !== user?.id;
    }

    // For other actions (like view, delete, like) by admins:
    // Admin users need an associated player for private comments
    if (comment && comment.is_private) {
      // Check if admin has player_id and is involved in the private conversation
      return hasAssociatedPlayer && (
        (comment.user && comment.user.id === user?.id) ||
        (comment.recipient_id === user?.id)
      );
    }

    // Admin can interact with public comments even without player_id
    return true;
  };

  const renderComment = (comment, depth = 0) => {
    // Ensure comment.user exists
    if (!comment || !comment.user) {
      return null;
    }

    const isEditing = activeEditFormId === comment.id;
    const isReplying = activeReplyFormId === comment.id;

    const directReplies = getDirectReplies(comment.id);
    const hasReplies = directReplies.length > 0;
    const isExpanded = expandedComments[comment.id];

    // Calculate indentation and styling based on nesting level
    const indentationStyles = {
      marginLeft: depth > 0 ? `${20 * Math.min(depth, 3)}px` : "0",
      borderLeft: depth > 0 ? "2px solid #e9ecef" : "none",
      paddingLeft: depth > 0 ? "15px" : "0",
    };

    // Smaller avatars for nested replies
    const avatarSize = depth === 0 ? 69 : Math.max(69 - (depth * 8), 40);

    // Make sure user photo exists, use default if not
    const userPhoto = comment.user.photo || getDefaultAvatar();

    // Should show recipient label on general pages
    const shouldShowRecipientLabel = comment.is_private && comment.recipient_id && !isPlayerPage;
    // Should show recipient label on player's own page when they sent the comment
    const shouldShowSentToLabelOnPlayerPage = comment.is_private && comment.recipient_id && isPlayerPage && comment.user && comment.user.player_id === player_id;

    // Determine if the profile link should be clickable
    // Admin without player_id should not have a clickable profile link
    const hasProfileLink = comment.user && (
      !comment.user.admin ||
      (comment.user.admin && comment.user.player_id)
    );

    return (
      <div key={comment.id} className="comment-thread" style={{ marginTop: depth === 0 ? "20px" : "10px" }}>
        <div className="post-comment" style={indentationStyles}>
          <div className="post-comment-aside">
            <img
              className="img-circle"
              src={userPhoto}
              width={avatarSize}
              height={avatarSize}
              alt="User avatar"
            />
          </div>
          <div className="post-comment-main">
            <div className="post-comment-header">
              {hasProfileLink ? (
                <a href={`/${getLocaleFromURL()}/players/${comment.user.player_id}`}>
                  <h5 className="author-name">
                    {getUserDisplayName(comment.user)}
                  </h5>
                </a>
              ) : (
                <h5 className="author-name">
                  {getUserDisplayName(comment.user)}
                </h5>
              )}
              <time className="post-comment-time" dateTime={comment.created_at} title={new Date(comment.created_at).toLocaleString()}>
                {getTimeDifference(comment.created_at)}
              </time>
            </div>

            {/* Show inline EDIT form OR comment content */}
            {isEditing ? (
              <div className="inline-comment-form mt-2 mb-2">
                <AddComment
                  user={user}
                  t_controls={t_controls}
                  player_id={player_id} // Context player ID
                  playersList={playersList}
                  privateTab={comment.is_private} // Pass comment's privacy status
                  edited_comment={comment} // Use correct prop name
                  onCancelReply={handleCancelInlineForm} // Use correct prop name
                  setComms={onCommentsChange} // Pass function to update comments list
                  event_id={event_id} // Pass event_id if available
                  isPlayerPage={isPlayerPage} // Pass isPlayerPage context
                />
              </div>
            ) : (
              <>
                {/* Add recipient information for private messages */}
                {(shouldShowRecipientLabel || shouldShowSentToLabelOnPlayerPage) && (
                  <div className="private-message-recipient" style={{
                    backgroundColor: '#f8f9fa',
                    borderLeft: '3px solid #dc3545',
                    padding: '4px 8px',
                    marginBottom: '8px',
                    fontSize: '0.85em',
                    borderRadius: '2px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <span className="icon mdi mdi-account-arrow-right" style={{ marginRight: '5px', color: '#dc3545' }}></span>
                    <span>{t_controls.sent_to || "Sent to"}: {getRecipientName(comment.recipient_id)}</span>
                  </div>
                )}

                {comment.parent_id && (
                  <div className="p-2 mb-2 rounded w-100" style={{
                    backgroundColor: '#f8f9fa',
                    borderLeft: '3px solid #6c757d',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    fontSize: '0.9em',
                    color: '#495057'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
                      <span className="icon mdi mdi-reply" style={{ marginRight: '5px', opacity: 0.6 }}></span>
                      <span style={{ fontSize: '0.85em', opacity: 0.7 }}>
                        {t_controls.replying_to} {comments.find(parent => parent.id === comment.parent_id)?.user?.player
                          ? `${comments.find(parent => parent.id === comment.parent_id)?.user?.player?.name}`
                          : 'comment'}
                      </span>
                    </div>
                    <p className="m-0" style={{ fontStyle: 'italic' }}>
                      {comments.find(parent => parent.id === comment.parent_id)?.text}
                    </p>
                  </div>
                )}

                <div className="post-comment-text"
                  style={{
                    backgroundColor: "#ffffff",
                    padding: "10px",
                    borderRadius: "4px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                  }}
                >
                  <p className="label" style={{ textAlign: 'left', color: '#212529', margin: '0' }}>{comment.text}</p>
                </div>
                <div className="post-comment-footer">
                  <a href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      // Allow liking only if NOT editing/replying to THIS comment
                      if (!isEditing && !isReplying && canInteractWithComments(comment)) handleLike(comment.id);
                    }}
                    style={{
                      // pointerEvents: (isInlineFormActive() || !canInteractWithComments(comment)) ? 'none' : 'auto',
                      // opacity: (isInlineFormActive() || !canInteractWithComments(comment)) ? 0.5 : 1,
                      // cursor: (isInlineFormActive() || !canInteractWithComments(comment)) ? 'not-allowed' : 'pointer'
                      pointerEvents: (isEditing || isReplying || !canInteractWithComments(comment)) ? 'none' : 'auto',
                      opacity: (isEditing || isReplying || !canInteractWithComments(comment)) ? 0.5 : 1,
                      cursor: (isEditing || isReplying || !canInteractWithComments(comment)) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <div className="comment-like" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span className={comment.user_likes && Array.isArray(comment.user_likes) && comment.user_likes.includes(comment.id) ? "icon mdi mdi-thumb-up" : "icon mdi mdi-thumb-up-outline"}></span>
                      <span id={`likes_${comment.id}`}>{comment.likes_count}</span>
                    </div>
                  </a>
                  <a href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      // Allow replying only if NOT editing/replying to THIS comment
                      if (!isEditing && !isReplying && canInteractWithComments(comment, 'reply')) onReplyClick(comment);
                    }}
                    style={{
                      // pointerEvents: (isInlineFormActive() || !canInteractWithComments(comment, 'reply')) ? 'none' : 'auto',
                      // opacity: (isInlineFormActive() || !canInteractWithComments(comment, 'reply')) ? 0.5 : 1,
                      // cursor: (isInlineFormActive() || !canInteractWithComments(comment, 'reply')) ? 'not-allowed' : 'pointer'
                      pointerEvents: (isEditing || isReplying || !canInteractWithComments(comment, 'reply')) ? 'none' : 'auto',
                      opacity: (isEditing || isReplying || !canInteractWithComments(comment, 'reply')) ? 0.5 : 1,
                      cursor: (isEditing || isReplying || !canInteractWithComments(comment, 'reply')) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <div className="comment-reply" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span className="icon mdi mdi-comment-outline"></span>
                      <span>{t_controls.reply}</span>
                    </div>
                  </a>
                  {hasReplies && (
                    <a href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        // Allow toggle only if NOT editing/replying to THIS comment
                        if (!isEditing && !isReplying) toggleReplies(comment.id);
                      }}
                      style={{
                        // pointerEvents: isInlineFormActive() ? 'none' : 'auto',
                        // opacity: isInlineFormActive() ? 0.5 : 1,
                        // cursor: isInlineFormActive() ? 'not-allowed' : 'pointer'
                        pointerEvents: (isEditing || isReplying) ? 'none' : 'auto',
                        opacity: (isEditing || isReplying) ? 0.5 : 1,
                        cursor: (isEditing || isReplying) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span className={`icon mdi ${isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'}`}></span>
                        <span>
                          {isExpanded
                            ? t_controls.hide_replies || "Hide replies"
                            : t_controls.show_replies || "Show replies"}
                          ({getRepliesCount(comment.id)})
                        </span>
                      </div>
                    </a>
                  )}
                  {comment.user && user && (user.id === comment.user.id || (user.admin && canInteractWithComments(comment, 'edit'))) && (
                    <a href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        // Allow editing only if NOT editing/replying to THIS comment
                        if (!isEditing && !isReplying && canInteractWithComments(comment, 'edit')) onEditClick(comment);
                      }}
                      style={{
                        // pointerEvents: (isInlineFormActive() || !canInteractWithComments(comment, 'edit')) ? 'none' : 'auto',
                        // opacity: (isInlineFormActive() || !canInteractWithComments(comment, 'edit')) ? 0.5 : 1,
                        // cursor: (isInlineFormActive() || !canInteractWithComments(comment, 'edit')) ? 'not-allowed' : 'pointer'
                        pointerEvents: (isEditing || isReplying || !canInteractWithComments(comment, 'edit')) ? 'none' : 'auto',
                        opacity: (isEditing || isReplying || !canInteractWithComments(comment, 'edit')) ? 0.5 : 1,
                        cursor: (isEditing || isReplying || !canInteractWithComments(comment, 'edit')) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span className="icon mdi mdi-pencil-outline"></span>
                        <span>{t_controls.edit}</span>
                      </div>
                    </a>
                  )}
                  {comment.user && user && (user.id === comment.user.id || (user.admin && canInteractWithComments(comment))) && (
                    <a href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        // Allow deleting only if NOT editing/replying to THIS comment
                        if (!isEditing && !isReplying && canInteractWithComments(comment)) openModal(comment.id);
                      }}
                      style={{
                        // pointerEvents: (isInlineFormActive() || !canInteractWithComments(comment)) ? 'none' : 'auto',
                        // opacity: (isInlineFormActive() || !canInteractWithComments(comment)) ? 0.5 : 1,
                        // cursor: (isInlineFormActive() || !canInteractWithComments(comment)) ? 'not-allowed' : 'pointer'
                        pointerEvents: (isEditing || isReplying || !canInteractWithComments(comment)) ? 'none' : 'auto',
                        opacity: (isEditing || isReplying || !canInteractWithComments(comment)) ? 0.5 : 1,
                        cursor: (isEditing || isReplying || !canInteractWithComments(comment)) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span className="icon mdi mdi-trash-can-outline"></span>
                        <span>{t_controls.delete}</span>
                      </div>
                    </a>
                  )}
                </div>
              </>
            )}

            {/* Show inline REPLY form */}
            {isReplying && (
              <div className="inline-comment-form mt-2 mb-2" style={{ marginLeft: "15px", borderLeft: "2px solid #ced4da", paddingLeft: "15px" }}>
                <AddComment
                  user={user}
                  t_controls={t_controls}
                  player_id={player_id} // Context player ID (used for recipient default?)
                  playersList={playersList}
                  privateTab={comment.is_private} // Inherit privacy from parent
                  parent_comment={comment} // Use correct prop name
                  onCancelReply={handleCancelInlineForm} // Use correct prop name
                  setComms={onCommentsChange} // Pass function to update comments list
                  event_id={event_id} // Pass event_id if available
                  isPlayerPage={isPlayerPage} // Pass isPlayerPage context
                />
              </div>
            )}

          </div>
        </div>

        {/* Render nested replies recursively if expanded */}
        {/* Conditionally render replies based on editing/replying state */}
        {isExpanded && hasReplies && !isEditing && !isReplying && (
          <div className="nested-replies" style={{
            marginLeft: "20px",
            marginTop: "5px",
            transition: "all 0.3s ease"
          }}>
            {directReplies.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderEmptyState = (isPrivate) => {
    return (
      <div className="empty-comments-state" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        margin: '20px 0'
      }}>
        <span className={`icon mdi ${isPrivate ? 'mdi-message-text-lock-outline' : 'mdi-message-text-outline'}`} style={{
          fontSize: '2.5rem',
          color: '#dc3545',
          opacity: 0.7,
          marginBottom: '15px'
        }}></span>
        <p style={{
          fontSize: '1rem',
          color: '#6c757d',
          textAlign: 'center',
          margin: 0
        }}>
          {isPrivate
            ? t_controls.no_private_comments_message || "No private comments yet. Be the first to start a private conversation."
            : t_controls.no_public_comments_message || "No public comments yet. Start the conversation!"}
        </p>
      </div>
    );
  };

  // Filter accessible private comments for admin
  const filterPrivateCommentsForAdmin = (comment) => {
    if (!user?.admin) {
      // Non-admin can see private comments where they are sender or recipient
      return (!comment.is_private ||
        (comment.user && comment.user.id === user?.id) ||
        comment.recipient_id === user?.id);
    }

    // Admin with no player_id can't see private comments
    if (!user?.player_id) return !comment.is_private;

    // Admin with player_id can see private comments where their player is involved
    return (!comment.is_private ||
      (comment.user && comment.user.player_id === user?.player_id) ||
      comment.recipient_id === user?.player_id);
  };

  const renderComments = (commentList) => {
    if (commentList.length > 0) {
      // Filter accessible comments
      const visibleComments = commentList.filter(comment =>
        filterPrivateCommentsForAdmin(comment) && comment.user
      );

      if (visibleComments.length === 0) {
        return renderEmptyState(privateTab);
      }

      // Filter only top-level comments (those without a parent_id)
      const topLevelComments = visibleComments.filter(comment => !comment.parent_id);

      if (topLevelComments.length > 0) {
        return (
          <div className="comments-container">
            {topLevelComments.map(comment => renderComment(comment))}
          </div>
        );
      } else {
        // If there are no top-level comments but there are replies, show all comments
        return (
          <div className="comments-container">
            {visibleComments.map(comment => renderComment(comment))}
          </div>
        );
      }
    } else {
      return renderEmptyState(privateTab);
    }
  };

  return (
    <>
      <div>
        <article className="heading-component">
          <div className="heading-component-inner">
            <h5 className="heading-component-title">
              {isPlayerPage
                ? t_controls.private_comments || "Private comments"
                : (privateTab ? t_controls.private_comments || "Private comments" : t_controls.public_comments || "Public comments")
              }
            </h5>

            {/* Only show tabs on game pages, not on player pages */}
            {!isPlayerPage && (
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <button
                    className="button button-primary"
                    style={{
                      backgroundColor: !privateTab ? '#dc3545' : '#f8a4a4',
                      color: 'white',
                      fontWeight: !privateTab ? 'bold' : 'normal',
                      border: 'none',
                      borderRadius: '4px 4px 0 0',
                      padding: '10px 15px',
                      marginRight: '5px',
                      transition: 'background-color 0.3s ease',
                      // opacity: (isInEditOrReplyMode() || !canInteractWithComments()) ? 0.5 : 1,
                      // cursor: (isInEditOrReplyMode() || !canInteractWithComments()) ? 'not-allowed' : 'pointer'
                      opacity: (isInlineFormActive() || !canInteractWithComments()) ? 0.5 : 1,
                      cursor: (isInlineFormActive() || !canInteractWithComments()) ? 'not-allowed' : 'pointer'
                    }}
                    // onClick={() => { if (!isInEditOrReplyMode() && canInteractWithComments()) setPrivateTab(false); }}
                    // disabled={isInEditOrReplyMode() || !canInteractWithComments()}
                    onClick={() => { if (!isInlineFormActive() && canInteractWithComments()) setPrivateTab(false); }}
                    disabled={isInlineFormActive() || !canInteractWithComments()}
                  >
                    {t_controls.public} ({publicCommentsCount})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className="button button-primary"
                    style={{
                      backgroundColor: privateTab ? '#dc3545' : '#f8a4a4',
                      color: 'white',
                      fontWeight: privateTab ? 'bold' : 'normal',
                      border: 'none',
                      borderRadius: '4px 4px 0 0',
                      padding: '10px 15px',
                      transition: 'background-color 0.3s ease',
                      // opacity: (isInEditOrReplyMode() || !canInteractWithComments()) ? 0.5 : 1,
                      // cursor: (isInEditOrReplyMode() || !canInteractWithComments()) ? 'not-allowed' : 'pointer'
                      opacity: (isInlineFormActive() || !canInteractWithComments()) ? 0.5 : 1,
                      cursor: (isInlineFormActive() || !canInteractWithComments()) ? 'not-allowed' : 'pointer'
                    }}
                    // onClick={() => { if (!isInEditOrReplyMode() && canInteractWithComments()) setPrivateTab(true); }}
                    // disabled={isInEditOrReplyMode() || !canInteractWithComments()}
                    onClick={() => { if (!isInlineFormActive() && canInteractWithComments()) setPrivateTab(true); }}
                    disabled={isInlineFormActive() || !canInteractWithComments()}
                  >
                    {t_controls.private} ({privateCommentsCount})
                  </button>
                </li>
              </ul>
            )}
          </div>
        </article>

        <div>
          {comments.length > 0 ? (
            <div>
              {privateTab ?
                (privateCommentsCount > 0 ?
                  renderComments(comments.filter(comment => comment.is_private && comment.user)) :
                  renderEmptyState(true)
                ) :
                (publicCommentsCount > 0 ?
                  renderComments(comments.filter(comment => !comment.is_private && comment.user)) :
                  renderEmptyState(false)
                )
              }
            </div>
          ) : (
            renderEmptyState(privateTab)
          )}
        </div>
      </div>

      {modalRef && (
        <div className="modal fade" ref={modalRef} id="deleteModal" tabIndex="-1" aria-hidden="true">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-body">
                <h5>{t_controls.modal_question}</h5>
              </div>
              <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                <button type="button" className="button button-primary" data-bs-dismiss="modal" onClick={() => closeModal()}>
                  {t_controls.modal_cancel}
                </button>
                <button type="button" className="button button-primary" style={{ marginTop: "-0px" }} onClick={() => handleDelete(commentToDelete)}>
                  {/* Use t_controls.modal_confirm if available, else default */}
                  {t_controls.modal_confirm || t_controls.delete || 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Comments;
